import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "./Icon";

function parseOption(option) {
  if (option && typeof option === "object") {
    const optionValue = option.value ?? option.id ?? option.key ?? "";
    return {
      value: String(optionValue),
      label: option.label ?? option.title ?? String(optionValue),
    };
  }

  return { value: String(option ?? ""), label: String(option ?? "") };
}

function parseValue(valueItem) {
  if (valueItem && typeof valueItem === "object") {
    return String(valueItem.value ?? valueItem.id ?? valueItem.key ?? "");
  }
  return String(valueItem ?? "");
}

const TagsView = ({
  value = [],
  onChange,
  options = [],
  disabled = false,
  className = "",
}) => {
  const [searchText, setSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const parsedOptions = useMemo(
    () => options.map(parseOption).filter((i) => i.value),
    [options],
  );
  const selectedValues = useMemo(
    () => (Array.isArray(value) ? value.map(parseValue).filter(Boolean) : []),
    [value],
  );
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const selectedFromOptions = parsedOptions.filter((i) =>
    selectedSet.has(i.value),
  );
  const selectedMissingOptions = selectedValues
    .filter((i) => !parsedOptions.some((option) => option.value === i))
    .map((i) => ({ value: i, label: i }));
  const selectedTags = [...selectedFromOptions, ...selectedMissingOptions];

  const filteredOptions = parsedOptions.filter((i) => {
    if (selectedSet.has(i.value)) return false;
    if (!searchText.trim()) return true;
    return i.label.toLowerCase().includes(searchText.trim().toLowerCase());
  });

  useEffect(() => {
    const handleMouseDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchText, filteredOptions.length]);

  const removeTag = (removedValue) => {
    if (!onChange) return;
    onChange(selectedValues.filter((i) => i !== removedValue));
  };

  const addTag = (addedValue) => {
    if (!onChange) return;
    if (selectedSet.has(addedValue)) return;
    onChange([...selectedValues, addedValue]);
    setSearchText("");
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Backspace" && !searchText && selectedValues.length > 0) {
      removeTag(selectedValues[selectedValues.length - 1]);
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setActiveIndex((prev) =>
        Math.min(prev + 1, Math.max(filteredOptions.length - 1, 0)),
      );
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }
      setActiveIndex((prev) => Math.max(prev - 1, 0));
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (e.key === "Enter" && filteredOptions.length > 0) {
      e.preventDefault();
      const option = filteredOptions[activeIndex] || filteredOptions[0];
      if (option) addTag(option.value);
    }
  };

  return (
    <div
      className={`relative ${className} ${isOpen ? "z-20" : "z-0"}`}
      ref={containerRef}
    >
      <div
        className={`w-full rounded-lg border border-gray-200 bg-white dark:bg-gray-800 dark:border-gray-700 px-2 py-1 min-h-[30px] input-focus-primary-gradient-within flex flex-col
            ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-text"}
            `}
        onClick={() => {
          if (disabled) return;
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <div className="flex items-center flex-wrap gap-1 flex-1">
          {selectedTags.map((tag) => (
            <span
              key={tag.value}
              className="flex items-center border border-sky-400 rounded-full bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-200 px-3 py-0.5 leading-normal"
            >
              <span className="text-[11px]">{tag.label}</span>
              <button
                type="button"
                disabled={disabled}
                className="ml-1 text-sm opacity-70 hover:opacity-100 text-red-500 disabled:cursor-not-allowed leading-none"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag.value);
                }}
              >
                &times;
              </button>
            </span>
          ))}
          <span className="text-gray-500 text-xs pl-1">🔍</span>

          <input
            ref={inputRef}
            type="text"
            className="search-bar flex-1 min-w-[120px] text-xs bg-transparent dark:text-gray-200 placeholder-gray-300 dark:placeholder-gray-600 outline-none border-none"
            disabled={disabled}
            value={searchText}
            onFocus={() => setIsOpen(true)}
            onChange={(e) => {
              setSearchText(e.target.value);
              if (!isOpen) setIsOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={selectedTags.length > 0 ? "" : "Search tags"}
          />

          <button
            type="button"
            disabled={disabled}
            className={`ml-auto pr-1 text-gray-600 hover:text-gray-900 active:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 ${
              String(searchText).trim() === ""
                ? "opacity-0 invisible"
                : "opacity-100 visible"
            }`}
            onClick={(e) => {
              if (disabled) return;
              e.stopPropagation();
              setSearchText("");
              inputRef.current?.focus();
            }}
          >
            <Icon icon="close" size={12} />
          </button>
        </div>
      </div>

      {isOpen && !disabled && (
        <div className="absolute mt-1 w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden">
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-4 text-xs text-gray-500 dark:text-gray-500">
              No tags found
            </div>
          ) : (
            <div className="max-h-72 overflow-auto">
              {filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  type="button"
                  className={`block w-full whitespace-normal text-left px-4 py-2 border-b border-gray-100 dark:border-gray-700 dark:text-gray-200 ${
                    activeIndex === index
                      ? "bg-gray-100 dark:bg-gray-700"
                      : "hover:bg-gray-50 dark:hover:bg-gray-700"
                  }`}
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addTag(option.value);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TagsView;
